<template>
  <div class="tw-flex tw-flex-col tw-gap-[12px]">
    <label
      id="label"
      class="apTablet2:tw-text-body-bold-desktop tw-text-body-bold-mobile "
      :class="{
        '!tw-text-ap-nero': isForceLightMode,
      }"
    >{{ title }}
    </label>

    <Dropdown ref="dropdownRef" :manual="true" :border="false" @visible-change="onVisibleChange">
      <template #trigger>
        <button
          ref="dropdownButton"
          type="button"
          :class="[
            'tw-w-full apDesktop:tw-h-16 tw-h-12',
            'tw-outline tw-outline-ap-gainsboro tw-outline-offset-[-2px] tw-outline-1',
            'hover:tw-outline-ap-dodger-blue focus:tw-outline-ap-dodger-blue hover:tw-outline-2 focus:tw-outline-2',
            'apDesktop:tw-rounded-xl tw-rounded-[10px] tw-bg-white  tw-text-ap-nero tw-flex tw-justify-between tw-items-center tw-transition',
            'apTablet2:tw-text-body-md-desktop tw-text-body-md-mobile',
            'tw-pl-6 tw-pr-4',
            {
              '!tw-bg-white !tw-outline-ap-gainsboro': isForceLightMode,
            },
          ]"
          @click="onClickDropdown"
        >
          <span
            class=""
            :class="{
              '!tw-text-ap-nero': isForceLightMode,
            }"
          >{{ data.length > 0 ? data : 'Please select' }}</span>
          <img class="tw-w-6 tw-h-6" :src="getCaretIconSrc()">
        </button>
      </template>

      <div
        :class="[
          'tw-max-h-[250px] tw-overflow-y-auto tw-p-2 tw-bg-white  tw-z-50',
          {
            '!tw-bg-white': isForceLightMode,
          },
        ]"
        :style="{
          minWidth: menuWidth + 'px',
        }"
      >
        <div v-for="(option, index) in options" :key="`option-${index}`">
          <button
            type="button"
            class="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-ap-nero tw-font-inter apTablet2:tw-text-body-md-desktop tw-text-body-md-mobile tw-leading-[32px] tw-transition hover:tw-bg-ap-dodger-blue hover:tw-text-white tw-rounded-md"
            :class="{ 'tw-bg-ap-dodger-blue tw-text-white': dropdownSelectIndex === index }"
            @pointerdown="onTouchStart($event, option)"
          >
            <div class="tw-flex tw-justify-between tw-items-center">
              <span>{{ option }}</span>
              <img
                v-if="dropdownSelectIndex === index"
                class="tw-w-5 tw-h-5"
                :src="$cdn('icons/contact-us/icon_check_light.svg')"
              >
            </div>
          </button>
        </div>
      </div>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import Dropdown from 'v-dropdown';
import { onMounted, ref } from 'vue';
import { cdn } from '../../support/cdn';
import { getDarkMode, isMobileWidth } from '../../support/helper';

const dropdownRef = ref<HTMLElement | null>(null);
const dropdownSelectIndex = ref(-1);
const dropdownButton = ref<HTMLElement | null>(null);
const isDarkMode = ref(false);

const emit = defineEmits(['update:data']);
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  formContainer: {
    type: Object,
    default: undefined,
  },
  options: {
    type: Array as () => string[],
    required: true
  },
  data: {
    type: String,
    default: ''
  },
  isForceLightMode: {
    type: Boolean,
    default: false
  }
});

const activeWindow = ref(false);
const menuWidth = ref(500);

onMounted(() => {
  isDarkMode.value = getDarkMode();

  if (props.options.includes(props.data)) {
    dropdownSelectIndex.value = props.options.indexOf(props.data);
  }

  if (!isMobileWidth) {
    dropdownStyle.value = {
      width: dropdownRef.value?.offsetWidth + 'px',
    };
  }

  // Set the selection width to the width of the dropdown button
  if (dropdownButton.value) {
    const fullWidth = dropdownButton.value?.getBoundingClientRect().width;
    menuWidth.value = fullWidth;
  }
});

function onClickDropdown(_: any) {
  if (dropdownRef.value) {
    if (activeWindow.value) {
      (dropdownRef.value as any).close();
    } else {
      (dropdownRef.value as any).display();
    }
  }
}

function onTouchStart(event: any, option: any) {
  // Trigger the selection immediately on touch start
  onSelectDropdownItem(option);
  // Prevent the default long-press actions like context menus or text selection
  event.preventDefault();
}

// function onClick(option: any) {
//   // Trigger the selection for desktop users or regular click events
//   onSelectDropdownItem(option);
// }

function onSelectDropdownItem(value: any) {
  dropdownSelectIndex.value = props.options.indexOf(value);
  emit('update:data', value);
  if (dropdownRef.value) {
    (dropdownRef.value as any).close();
  }
}

function handleScroll() {
  if (dropdownRef.value) {
    (dropdownRef.value as any).close();
  }
}

const dropdownStyle = ref({
  width: '100%',
});

function onVisibleChange(visible: boolean) {
  activeWindow.value = visible;
  if (visible) {
    if (props.formContainer) {
      props.formContainer.addEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('keydown', handleKeyEvent);
  } else {
    if (props.formContainer) {
      props.formContainer.removeEventListener('scroll', handleScroll);
    } else {
      window.removeEventListener('scroll', handleScroll);
    }
    window.removeEventListener('keydown', handleKeyEvent);
  }
}

// Function to handle keyboard events for the country code dropdown
function handleKeyEvent(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      if (dropdownSelectIndex.value === props.options.length - 1) {
        return;
      }
      dropdownSelectIndex.value = (dropdownSelectIndex.value + 1) % props.options.length;
      scrollToActiveItem(event.key);
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (dropdownSelectIndex.value === 0) {
        return;
      }
      dropdownSelectIndex.value = (dropdownSelectIndex.value - 1 + props.options.length) % props.options.length;
      scrollToActiveItem(event.key);
      break;
    case 'Enter':
      event.preventDefault();
      onSelectDropdownItem(props.options[dropdownSelectIndex.value]);
      break;
  }
}

// Function to scroll to the active item in the dropdown
const scrollToActiveItem = (key: any) => {
  const dropdown = document.querySelector('.dropdown-content');
  const activeItem = document.querySelector('.dropdown-item.active');

  if (dropdown && activeItem) {
    const dropdownRect = dropdown.getBoundingClientRect();
    const activeRect = activeItem.getBoundingClientRect();

    const difference = activeRect.top - dropdownRect.top;
    if (difference > 128 && key === 'ArrowDown') {
      dropdown.scrollTop += 32;
    } else if (difference < 32 && key === 'ArrowUp') {
      dropdown.scrollTop -= 32;
    }
  }
};

function getCaretIconSrc() {
  if (props.isForceLightMode) {
    return cdn('icons/contact-us/icon_caret_down.svg');
  } else if (isDarkMode.value) {
    return cdn('icons/contact-us/icon_caret_down.svg');
  } else {
    return cdn('icons/contact-us/icon_caret_down.svg');
  }
}

</script>

<style scoped></style>